
import { useMemo } from 'react';
import { format } from 'date-fns';

interface BookingTimeCalculationsProps {
  booking: any;
  currentTime: Date;
  checkInWindowMinutes: number;
  isCompleted: boolean;
  isCancelled: boolean;
  isCheckedIn: boolean;
}

export const useBookingTimeCalculations = ({
  booking,
  currentTime,
  checkInWindowMinutes,
  isCompleted,
  isCancelled,
  isCheckedIn
}: BookingTimeCalculationsProps) => {
  // Format timestamp with friendly formatting
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const isSameDay = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
      
      if (isSameDay) {
        return `Today at ${format(date, 'h:mm a')}`;
      } else {
        return `${format(date, 'MMM d')} at ${format(date, 'h:mm a')}`;
      }
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return timestamp;
    }
  };

  // Get actual check-in time from timestamp
  const actualCheckInTime = useMemo(() => {
    console.log('=== CALCULATING ACTUAL CHECK-IN TIME ===');
    console.log('Booking checked_in_at:', booking?.checked_in_at);
    console.log('isCompleted:', isCompleted);
    console.log('isCheckedIn:', isCheckedIn);
    
    if (booking?.checked_in_at) {
      const formattedTime = formatTimestamp(booking.checked_in_at);
      console.log('Formatted check-in time:', formattedTime);
      return formattedTime;
    }
    
    if (isCompleted && !booking?.checked_in_at) {
      console.log('Completed booking - no check-in timestamp found, showing generic message');
      return 'Session completed';
    }
    
    console.log('No check-in time found, returning null');
    return null;
  }, [booking?.checked_in_at, isCompleted, isCheckedIn]);

  // Get actual check-out time from timestamp
  const actualCheckOutTime = useMemo(() => {
    console.log('=== CALCULATING ACTUAL CHECK-OUT TIME ===');
    console.log('Booking checked_out_at:', booking?.checked_out_at);
    console.log('isCompleted:', isCompleted);
    
    if (booking?.checked_out_at) {
      const formattedTime = formatTimestamp(booking.checked_out_at);
      console.log('Formatted check-out time:', formattedTime);
      return formattedTime;
    }
    
    // If booking is completed but no explicit check-out timestamp, use updated_at
    if (isCompleted && booking?.updated_at && !booking?.checked_out_at) {
      try {
        const formattedTime = formatTimestamp(booking.updated_at);
        console.log('Using booking updated_at for check-out time:', formattedTime);
        return formattedTime;
      } catch (error) {
        console.log('Error parsing updated_at for check-out time:', error);
      }
    }
    
    console.log('No check-out time found, returning null');
    return null;
  }, [booking?.checked_out_at, booking?.updated_at, isCompleted]);

  // Calculate time-based permissions
  const timeCalculations = useMemo(() => {
    if (!booking) return {};

    const bookingDateTime = new Date(`${booking.booking_date}T${booking.start_time}`);
    const sessionEnd = new Date(`${booking.booking_date}T${booking.end_time}`);
    const checkInAvailableTime = new Date(bookingDateTime.getTime() - checkInWindowMinutes * 60 * 1000);
    const cancellationDeadline = new Date(bookingDateTime.getTime() - 2 * 60 * 60 * 1000); // 2 hours before
    const rescheduleDeadline = new Date(bookingDateTime.getTime() - 2 * 60 * 60 * 1000); // 2 hours before

    const sessionHasEnded = currentTime > sessionEnd;
    const canCancel = !isCancelled && !isCompleted && !isCheckedIn && currentTime < cancellationDeadline;
    const canReschedule = !isCancelled && !isCompleted && !isCheckedIn && currentTime < rescheduleDeadline;
    const isPastCancellationDeadline = currentTime >= cancellationDeadline && !isCancelled && !isCompleted;

    console.log('=== CHECK IN AVAILABILITY DEBUG ===');
    console.log('Current time:', currentTime.toISOString());
    console.log('Booking date time:', bookingDateTime.toISOString());
    console.log('Session end:', sessionEnd.toISOString());
    console.log(`Check in available time (${checkInWindowMinutes} min before):`, checkInAvailableTime.toISOString());

    return {
      bookingDateTime,
      sessionEnd,
      checkInAvailableTime,
      cancellationDeadline,
      rescheduleDeadline,
      sessionHasEnded,
      canCancel,
      canReschedule,
      isPastCancellationDeadline
    };
  }, [booking, currentTime, checkInWindowMinutes, isCompleted, isCancelled, isCheckedIn]);

  return {
    actualCheckInTime,
    actualCheckOutTime,
    formatTimestamp,
    ...timeCalculations
  };
};
